import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCcw, Send, ChevronDown, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { EmptyState, ErrorState, TableSkeleton, StatusPill, fmtDate, plural } from '../../components/ui';
import { Notice } from '../Financials/financeKit';

// Timesheets › Employment Hero payroll: send a period's approved timesheets to Employment Hero,
// where they wait for approval and go into the pay run, and add staff who are not there yet.
// Employment Hero applies the award and pay rates from each employee's own pay settings; Rostery
// sends the worked times, break, position and the shift's details.
// Backend: routes/integrations.js (/employment-hero/*), rules in lib/employmentHero.js.

const STATUS = {
  ready: { tone: 'success', label: 'Ready' },
  in_eh: { tone: 'info', label: 'Already in Employment Hero' },
  not_in_eh: { tone: 'warning', label: 'Not in Employment Hero' },
  no_position: { tone: 'warning', label: 'No position' },
  refused: { tone: 'danger', label: 'Refused' },
};
const REASON = {
  not_in_eh: 'Not in Employment Hero yet. Add them from the Staff view, or use the same email in both.',
  no_position: 'Employment Hero needs a position on each timesheet. Assign this worker a position under Work Sites & Positions, or choose a position for workers without one above.',
};

// Local calendar dates (never toISOString, which gives the UTC date).
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const monday = (d) => addDays(d, -((d.getDay() + 6) % 7));
function presets() {
  const today = new Date();
  const thisMon = monday(today);
  return [
    ['This week', ymd(thisMon), ymd(today)],
    ['Last week', ymd(addDays(thisMon, -7)), ymd(addDays(thisMon, -1))],
    ['Last fortnight', ymd(addDays(thisMon, -14)), ymd(addDays(thisMon, -1))],
    ['Last 14 days', ymd(addDays(today, -14)), ymd(today)],
  ];
}

export default function EmploymentHeroPayrollTab() {
  const [status, setStatus] = useState(null);
  const [view, setView] = useState(() => (new URLSearchParams(window.location.search).get('view') === 'staff' ? 'staff' : 'timesheets'));
  useEffect(() => {
    api.get('/integrations/employment-hero/status')
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ connected: false }));
  }, []);

  if (!status) return <div className="card"><TableSkeleton rows={4} cols={6} /></div>;
  if (!status.connected) {
    return (
      <div className="card p-6">
        <EmptyState icon={Send} title="Employment Hero is not connected"
          hint="Connect Employment Hero to send timesheets to it."
          action={<Link to="/settings?tab=integrations" className="btn-primary btn-sm">Go to integrations</Link>} />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" role="tablist" aria-label="Employment Hero">
        {[['timesheets', 'Timesheets'], ['staff', 'Staff']].map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={view === id} onClick={() => setView(id)}
            className={`px-3 py-1.5 text-sm rounded-md font-medium ${view === id ? 'bg-rose-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>{label}</button>
        ))}
      </div>
      {view === 'timesheets' ? <TimesheetsView status={status} onStaff={() => setView('staff')} /> : <StaffView />}
    </div>
  );
}

function TimesheetsView({ status, onStaff }) {
  const confirm = useConfirm();
  const [[from, to], setRange] = useState(() => presets()[3].slice(1));
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState(null);

  const load = useCallback(async () => {
    if (!from || !to) return;
    setLoading(true); setError(null);
    try {
      const { data } = await api.post('/integrations/employment-hero/sync', { dry: true, from, to });
      setPreview(data);
      setPicked(new Set((data.workers || []).filter((w) => w.status === 'ready').map((w) => w.userId)));
    } catch (e) { setError(e.response?.data?.message || 'Could not prepare the timesheets.'); }
    finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const [positionOpen, setPositionOpen] = useState(false);
  const loadOptions = useCallback(() => {
    api.get('/integrations/employment-hero/options').then((r) => setOpts(r.data)).catch(() => setOpts({ positions: [] }));
  }, []);
  useEffect(() => { loadOptions(); }, [loadOptions]);
  const savePosition = async (id) => {
    try {
      await api.post('/integrations/employment-hero/settings', { default_position_id: id || null });
      setOpts((o) => ({ ...o, defaultPositionId: id || null }));
      toast.success(id ? 'Default position saved' : 'Default position cleared');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
  };

  const workers = preview?.workers || [];
  const ready = workers.filter((w) => w.status === 'ready');
  const chosen = ready.filter((w) => picked.has(w.userId));
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const counts = chosen.reduce((a, w) => ({ add: a.add + w.toSend, change: a.change + w.changed, remove: a.remove + w.removing }), { add: 0, change: 0, remove: 0 });

  const send = async () => {
    const parts = [];
    if (counts.add) parts.push(plural(counts.add, 'new timesheet'));
    if (counts.change) parts.push(`${plural(counts.change, 'changed timesheet')} (replaced)`);
    if (counts.remove) parts.push(`${plural(counts.remove, 'un-approved timesheet')} (removed)`);
    const ok = await confirm(
      `${parts.join(', ')} for ${plural(chosen.length, 'worker')}, ${fmtDate(from)} to ${fmtDate(to)}. They arrive in Employment Hero waiting for approval; approve them there, then run the pay run.`,
      { title: 'Send to Employment Hero?', confirmLabel: 'Send to Employment Hero' });
    if (!ok) return;
    setBusy(true);
    try {
      const { data } = await api.post('/integrations/employment-hero/sync', { from, to, userIds: chosen.map((w) => w.userId) });
      if (data.errors?.length) toast.error(data.errors[0] + (data.errors.length > 1 ? ` (+${data.errors.length - 1} more)` : ''), { duration: 9000 });
      // A refusal for a missing position opens the position set-up.
      if ((data.errors || []).some((e) => /position cannot be blank/i.test(e))) setPositionOpen(true);
      if (data.synced || data.removed) toast.success(data.message);
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Sending to Employment Hero failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="card p-5 sm:p-6 space-y-4">
        <div className="flex flex-wrap items-start gap-3">
          <span className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center shrink-0 font-bold text-rose-600" aria-hidden="true">EH</span>
          <div className="flex-1 min-w-[220px]">
            <h3 className="text-base font-semibold text-slate-900">Send timesheets to Employment Hero</h3>
            <p className="text-sm text-slate-600 mt-1 max-w-[72ch]">
              Approved timesheets for the period go to {status.tenantName || 'Employment Hero'} with their start, end, break and shift details, waiting for approval there.
              Employment Hero works out the pay from each employee’s award and pay settings. A timesheet changed after sending is replaced; one no longer approved is removed.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-slate-100">
          <div>
            <label htmlFor="eh-from" className="text-[13px] text-slate-700 font-medium block mb-1">From</label>
            <input id="eh-from" type="date" className="input-field" value={from} max={to} onChange={(e) => setRange([e.target.value, to])} />
          </div>
          <div>
            <label htmlFor="eh-to" className="text-[13px] text-slate-700 font-medium block mb-1">To</label>
            <input id="eh-to" type="date" className="input-field" value={to} min={from} onChange={(e) => setRange([from, e.target.value])} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {presets().map(([label, f, t]) => (
              <button key={label} type="button" className={`btn-secondary btn-sm ${from === f && to === t ? 'ring-2 ring-rose-200' : ''}`} onClick={() => setRange([f, t])}>{label}</button>
            ))}
          </div>
          <button type="button" className="btn-secondary" onClick={load} disabled={loading || !from || !to}>
            {loading ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <RefreshCcw size={14} aria-hidden="true" />} Show hours
          </button>
        </div>
        {opts?.positions?.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-[13px] pt-3 border-t border-slate-100">
            <label htmlFor="eh-default-position" className="text-slate-700 font-medium">Position for workers without one</label>
            <select id="eh-default-position" className="input-field max-w-xs" value={opts.defaultPositionId || ''} onChange={(e) => savePosition(e.target.value)}>
              <option value="">None (those workers wait)</option>
              {opts.positions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}
      </div>

      {opts && !opts.positions?.length && !positionOpen && (
        <p className="text-[13px] text-slate-500">Does Employment Hero ask for a position on each timesheet? <button type="button" className="text-rose-700 font-medium hover:underline" onClick={() => setPositionOpen(true)}>Set up a position</button></p>
      )}
      <PositionSetup open={!!opts && !opts.positions?.length && positionOpen} onCreated={() => { setPositionOpen(false); loadOptions(); load(); }} />

      {preview?.noPosition?.length > 0 && (
        <Notice tone="amber" title="Some workers have no position in Employment Hero">
          Your Employment Hero uses work sites and positions, so each timesheet needs one. Assign these workers a position in Employment Hero, or choose a position for workers without one above.
        </Notice>
      )}

      <div className="card overflow-hidden">
        {loading && !preview ? <TableSkeleton rows={5} cols={6} />
          : error ? <ErrorState message={error} onRetry={load} />
          : !preview ? <EmptyState icon={Send} title="Choose a period" hint="Then Rostery shows each worker's approved hours for it." />
          : !workers.length ? <EmptyState icon={Send} title="No approved timesheets in this period" hint="Approve timesheets first, or choose another period." />
          : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                    <tr>
                      <th className="px-4 py-2.5 w-10"><span className="sr-only">Send</span></th>
                      <th className="px-3 py-2.5 font-medium">Worker</th>
                      <th className="px-3 py-2.5 font-medium">Position</th>
                      <th className="px-2 py-2.5 font-medium text-right">Shifts</th>
                      <th className="px-2 py-2.5 font-medium text-right">Hours</th>
                      <th className="px-3 py-2.5 font-medium">To send</th>
                      <th className="px-3 py-2.5 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {workers.map((w) => {
                      const st = STATUS[w.status] || STATUS.in_eh;
                      const todo = [w.toSend && `${w.toSend} new`, w.changed && `${w.changed} changed`, w.removing && `${w.removing} to remove`].filter(Boolean).join(', ');
                      return (
                        <tr key={w.userId} className="align-top">
                          <td className="px-4 py-3">
                            <input type="checkbox" aria-label={`Send ${w.name}`} disabled={w.status !== 'ready'} checked={picked.has(w.userId)} onChange={() => toggle(w.userId)} />
                          </td>
                          <td className="px-3 py-3">
                            <p className="font-medium text-slate-900">{w.name}</p>
                            {w.ehName && w.ehName !== w.name && <p className="text-[12px] text-slate-500">Employment Hero: {w.ehName}</p>}
                          </td>
                          <td className="px-3 py-3 text-slate-700">{w.position || <span className="text-slate-400">—</span>}</td>
                          <td className="px-2 py-3 text-right tabular-nums text-slate-700">{w.shifts}</td>
                          <td className="px-2 py-3 text-right tabular-nums text-slate-700">{Number(w.hours).toFixed(2).replace(/\.00$/, '')}</td>
                          <td className="px-3 py-3 text-slate-700">{todo || <span className="text-slate-400">{w.sent ? `${w.sent} sent` : '—'}</span>}</td>
                          <td className="px-3 py-3">
                            <StatusPill tone={st.tone} label={st.label} />
                            {REASON[w.status] && <p className="text-[12px] text-slate-500 mt-1 max-w-[18rem]">{REASON[w.status]}</p>}
                            {w.status === 'not_in_eh' && <button type="button" className="text-[12px] text-rose-700 font-medium mt-1 hover:underline" onClick={onStaff}>Add to Employment Hero</button>}
                            {w.errors?.map((e) => <p key={e} className="text-[12px] text-red-700 mt-1 max-w-[18rem]">{e}</p>)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
                <p className="text-sm text-slate-600 flex-1">
                  {fmtDate(from)} – {fmtDate(to)} · {plural(ready.length, 'worker')} ready{workers.length > ready.length ? `, ${workers.length - ready.length} not sent` : ''}.
                </p>
                <button type="button" className="btn-primary" disabled={!chosen.length || busy} onClick={send}>
                  {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Send size={14} aria-hidden="true" />}
                  Send {plural(chosen.length, 'worker')} to Employment Hero
                </button>
              </div>
            </>
          )}
      </div>
      {preview && (
        <details className="card p-4 text-[13px] text-slate-600">
          <summary className="cursor-pointer font-medium text-slate-800 inline-flex items-center gap-1">What is sent <ChevronDown size={13} aria-hidden="true" /></summary>
          <p className="mt-2 max-w-[80ch]">
            Each approved timesheet becomes one Employment Hero timesheet with the clock-on and clock-off times, an unpaid break of the recorded length placed in the middle of the shift,
            and a comment with the Rostery shift details: the client, the service, the rostered times, kilometres and the timesheet reference.
            Workers are matched by their Employment Hero link or email. When Employment Hero uses positions, each timesheet carries the worker’s position. A shift past midnight is split at midnight if Employment Hero does not accept overnight timesheets.
            Pay rates and award levels are not sent: Employment Hero applies the award from each employee’s pay settings.
          </p>
        </details>
      )}
    </div>
  );
}

const STAFF_STATUS = {
  ready: { tone: 'success', label: 'Ready to add' },
  in_eh: { tone: 'info', label: 'In Employment Hero' },
  missing_dob: { tone: 'warning', label: 'No date of birth' },
  missing_name: { tone: 'warning', label: 'Name incomplete' },
  contractor: { tone: 'neutral', label: 'Contractor' },
};
const STAFF_HINT = {
  missing_dob: 'Employment Hero requires a date of birth. Add it in their Rostery staff profile.',
  missing_name: 'Add their first and last name in Rostery.',
  contractor: 'Contractors need an ABN and business details: add them as a contractor in Employment Hero.',
};

// Rostery staff and whether each one is in Employment Hero; add the ones who are not.
function StaffView() {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [entity, setEntity] = useState('');
  const [location, setLocation] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data: d } = await api.get('/integrations/employment-hero/staff');
      setData(d);
      setEntity((v) => v || d.defaults?.employingEntityId || '');
      setLocation((v) => v || d.defaults?.workLocationId || '');
      setPicked(new Set());
    } catch (e) { setError(e.response?.data?.message || 'Could not read Employment Hero.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const staff = data?.staff || [];
  const ready = staff.filter((s) => s.status === 'ready');
  const chosen = ready.filter((s) => picked.has(s.userId));
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const allPicked = ready.length > 0 && chosen.length === ready.length;
  const toggleAll = () => setPicked(allPicked ? new Set() : new Set(ready.map((s) => s.userId)));

  const add = async (list = chosen) => {
    if (!list.length) return;
    const ok = await confirm(
      `${plural(list.length, 'employee file')} will be created in Employment Hero with each person's name, email and date of birth. Employment Hero does not email them until you invite them there. Then complete their pay details (award, pay rate, tax and super) in Employment Hero.`,
      { title: `Add ${plural(list.length, 'staff member')} to Employment Hero?`, confirmLabel: 'Add to Employment Hero' });
    if (!ok) return;
    setBusy(true);
    try {
      const { data: r } = await api.post('/integrations/employment-hero/staff/add', { userIds: list.map((s) => s.userId), employingEntityId: entity, workLocationId: location });
      const bad = r.results.filter((x) => !x.ok);
      const already = r.results.filter((x) => x.existing);
      if (r.added) toast.success(`${plural(r.added, 'staff member')} added to Employment Hero`);
      // Someone already in Employment Hero is never created twice: say so plainly.
      if (already.length) toast.error(`Already in Employment Hero, not created again: ${already.map((x) => x.name).join(', ')}`, { duration: 9000 });
      if (bad.length) toast.error(`${bad[0].name}: ${bad[0].error}${bad.length > 1 ? ` (+${bad.length - 1} more)` : ''}`, { duration: 9000 });
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Adding staff failed'); }
    finally { setBusy(false); }
  };

  if (error) return <div className="card"><ErrorState message={error} onRetry={load} /></div>;
  if (!data) return <div className="card"><TableSkeleton rows={5} cols={4} /></div>;
  return (
    <div className="space-y-4">
      <div className="card p-5 sm:p-6 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h3 className="text-base font-semibold text-slate-900">Staff in Employment Hero</h3>
          <button type="button" className="btn-primary" disabled={!ready.length || busy || !entity || !location} onClick={() => add(ready)}>
            {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <UserPlus size={14} aria-hidden="true" />}
            {ready.length ? `Add all ${plural(ready.length, 'ready staff member')} to Employment Hero` : 'Everyone ready is in Employment Hero'}
          </button>
        </div>
        <p className="text-sm text-slate-600 max-w-[72ch]">
          Timesheets go only to workers who are in Employment Hero. Add the others here: Rostery creates their employee file with name, email and date of birth.
          Award, pay rate, tax and super are set in Employment Hero.
        </p>
        <div className="flex flex-wrap items-end gap-3 pt-2">
          <div>
            <label htmlFor="eh-entity" className="text-[13px] text-slate-700 font-medium block mb-1">Employing entity</label>
            <select id="eh-entity" className="input-field" value={entity} onChange={(e) => setEntity(e.target.value)}>
              {data.employingEntities.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="eh-location" className="text-[13px] text-slate-700 font-medium block mb-1">Work location</label>
            <select id="eh-location" className="input-field" value={location} onChange={(e) => setLocation(e.target.value)}>
              {data.workLocations.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
          </div>
        </div>
      </div>
      <div className="card overflow-hidden">
        {!staff.length ? <EmptyState icon={UserPlus} title="No active staff" hint="Staff appear here once they are in Rostery." /> : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 w-10"><input type="checkbox" aria-label="Select every staff member ready to add" disabled={!ready.length} checked={allPicked} onChange={toggleAll} /></th>
                    <th className="px-3 py-2.5 font-medium">Staff member</th>
                    <th className="px-3 py-2.5 font-medium">Email</th>
                    <th className="px-3 py-2.5 font-medium">Date of birth</th>
                    <th className="px-3 py-2.5 font-medium">Employment Hero</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {staff.map((s) => {
                    const st = STAFF_STATUS[s.status] || STAFF_STATUS.in_eh;
                    return (
                      <tr key={s.userId} className="align-top">
                        <td className="px-4 py-3"><input type="checkbox" aria-label={`Add ${s.name}`} disabled={s.status !== 'ready'} checked={picked.has(s.userId)} onChange={() => toggle(s.userId)} /></td>
                        <td className="px-3 py-3 font-medium text-slate-900">{s.name}</td>
                        <td className="px-3 py-3 text-slate-600 break-all">{s.email}</td>
                        <td className="px-3 py-3 text-slate-600">{s.dob ? fmtDate(s.dob) : <span className="text-slate-400">—</span>}</td>
                        <td className="px-3 py-3">
                          <StatusPill tone={st.tone} label={st.label} />
                          {s.ehName && s.ehName !== s.name && <p className="text-[12px] text-slate-500 mt-1">As {s.ehName}</p>}
                          {STAFF_HINT[s.status] && <p className="text-[12px] text-slate-500 mt-1 max-w-[18rem]">{STAFF_HINT[s.status]}</p>}
                          {['missing_dob', 'missing_name'].includes(s.status) && <Link to={`/staff/${s.userId}/edit`} className="text-[12px] text-rose-700 font-medium hover:underline">Fix in their profile</Link>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
              <p className="text-sm text-slate-600 flex-1">{plural(staff.filter((s) => s.status === 'in_eh').length, 'staff member')} in Employment Hero · {ready.length} ready to add.</p>
              <button type="button" className="btn-primary" disabled={!chosen.length || busy || !entity || !location} onClick={() => add(chosen)}>
                {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <UserPlus size={14} aria-hidden="true" />}
                Add {plural(chosen.length, 'staff member')} to Employment Hero
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Employment Hero refuses timesheets without a position when Positions are on for timesheets.
// Create one from Rostery: Employment Hero needs a work site and a cost centre for it.
function PositionSetup({ open, onCreated }) {
  const [setup, setSetup] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState('Support Worker');
  const [site, setSite] = useState('');
  const [centre, setCentre] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get('/integrations/employment-hero/position-setup');
      setSetup(data);
      setSite((v) => v || data.workSites[0]?.id || '');
      setCentre((v) => v || data.costCentres[0]?.id || '');
    } catch (e) { setError(e.response?.data?.message || 'Could not read Employment Hero.'); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/integrations/employment-hero/positions', { name, workSiteId: site, costCentreId: centre });
      toast.success(data.message);
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not create the position'); }
    finally { setBusy(false); }
  };
  if (!open) return null;
  return (
    <div className="card p-5 space-y-3 border-amber-200">
      <h3 className="text-base font-semibold text-slate-900">Set up a position in Employment Hero</h3>
      <p className="text-sm text-slate-600 max-w-[72ch]">
        Your Employment Hero needs a position on every timesheet. Create one here and every staff member Rostery has added to Employment Hero is assigned to it.
      </p>
      {error ? <p className="text-sm text-red-700">{error}</p> : !setup ? <TableSkeleton rows={2} cols={3} /> : !setup.costCentres.length ? (
        <Notice tone="amber" title="Employment Hero has no cost centre yet">
          A position needs a cost centre, and Employment Hero only lets you create cost centres in Employment Hero itself:
          Settings › Organisation › Cost centres. Add one (for example "Disability support"), then
          <button type="button" className="ml-1 font-medium underline" onClick={load}>check again</button>.
        </Notice>
      ) : !setup.workSites.length ? (
        <Notice tone="amber" title="Employment Hero has no work site yet">
          Add a work site in Employment Hero (Work Sites &amp; Positions), then <button type="button" className="ml-1 font-medium underline" onClick={load}>check again</button>.
        </Notice>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="eh-pos-name" className="text-[13px] text-slate-700 font-medium block mb-1">Position</label>
            <input id="eh-pos-name" className="input-field" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
          </div>
          <div>
            <label htmlFor="eh-pos-site" className="text-[13px] text-slate-700 font-medium block mb-1">Work site</label>
            <select id="eh-pos-site" className="input-field" value={site} onChange={(e) => setSite(e.target.value)}>
              {setup.workSites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="eh-pos-centre" className="text-[13px] text-slate-700 font-medium block mb-1">Cost centre</label>
            <select id="eh-pos-centre" className="input-field" value={centre} onChange={(e) => setCentre(e.target.value)}>
              {setup.costCentres.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <button type="button" className="btn-primary" disabled={busy || !name.trim() || !site || !centre} onClick={create}>
            {busy && <Loader2 size={14} className="animate-spin" aria-hidden="true" />} Create position for {plural(setup.linkedStaff, 'staff member')}
          </button>
        </div>
      )}
    </div>
  );
}
